import { SAArtwork } from 'types/Artwork'
import { ArtworkInfo } from './types'
import { updateCriticalImagePath, capitalizeString } from 'lib/helpers'
import { getArtworkImageUrlByCode } from 'lib/artwork'

type ArtworkImageParams = {
  artwork: SAArtwork
  hasPrints?: boolean
}

type ImageUrlParams = {
  originalSrc: string
  priority?: boolean
  imageSuffix?: string
}

type ImageAltParams = {
  artistName: string
  category: string
  subject?: string | null
  styles?: ReadonlyArray<string> | null
}

export const DESKTOP_COLUMN_WIDTH = 320 // max width of Artwork Data

export const MOBILE_COLUMN_WIDTH = 140 // 2 Column Experience Mobile

export const SPAN_HEIGHT = 24 // grid-auto-rows: 25px;

export const ARTWORK_STATUS = {
  AVAILABLE: 'avail',
  RESERVED: 'reserved',
  SOLD: 'sold',
  UNAVAILABLE: 'unavail',
}

export const ARTWORK_STATUS_MAPPING = {
  avail: 'available',
  reserved: 'reserved',
  sold: 'sold',
  unavail: 'unavailable',
}

export const ARTWORK_CATEGORIES = {
  COLLAGE: 'Collage',
  DRAWINGS: 'Drawings',
  DIGITAL: 'Digital',
  INSTALLATION: 'Installation',
  MIXED_MEDIA: 'Mixed Media',
  PAINTINGS: 'Paintings',
  SCULPTURE: 'Sculpture',
  PHOTOGRAPHY: 'Photography',
  PRINTMAKING: 'Printmaking',
}

export const CATEGORY_MAPPING = {
  Paintings: 'Painting',
  Photography: 'Photograph',
  Printmaking: 'Print',
  Digital: 'Digital Art',
  ['Mixed-media']: 'Mixed Media',
}

export const getArtworkInfo = ({ artwork, hasPrints }: ArtworkImageParams): ArtworkInfo => {
  const {
    artworkId,
    artworkTitle,
    category,
    country,
    height,
    firstName,
    lastName,
    imageBaseUrl,
    positionID,
    originalStatus,
    listPrice,
    printUrl,
    productUrl,
    sku,
    subject,
    styles,
    width,
    printDimensions,
    printMaterials,
    printFrames,
  } = artwork

  return {
    artistName: `${firstName} ${lastName}`,
    artworkId,
    category,
    country,
    dataPosition: positionID || 0,
    hasPrints: hasPrints ?? false,
    height,
    href: hasPrints ? printUrl : productUrl,
    originalStatus,
    listPrice,
    productType: 'original',
    rel: artworkTitle,
    sku,
    src: imageBaseUrl,
    subject,
    styles,
    title: artworkTitle,
    width,
    printDimensions,
    printMaterials,
    printFrames,
  }
}

export const getImageSrc = ({
  originalSrc,
  priority,
  imageSuffix = 'pdp',
}: ImageUrlParams): string => {
  const IMAGE_SUBFOLDER = 'saatchi-images'
  const transformedImageSrc = getArtworkImageUrlByCode({
    imageUrl: originalSrc,
    code: imageSuffix,
  })

  return priority
    ? updateCriticalImagePath(IMAGE_SUBFOLDER, transformedImageSrc)
    : transformedImageSrc
}

export const getCleanCategory = (category: string) => {
  const cleanCategory = category?.replace(/s+$/, '')

  if (CATEGORY_MAPPING[cleanCategory]) {
    return CATEGORY_MAPPING[cleanCategory]
  }

  return cleanCategory
}

// Only the first value of styles is used and if it's the same as subject, uses the second value if exists. Discussed in this ticket: https://saatchiart.atlassian.net/browse/SA-4524
// example of result: `Original Fine Art Abstract Painting by John Doe`
export const getImageAltText = ({
  artistName,
  category,
  subject,
  styles,
}: ImageAltParams): string => {
  const [firstStyleNotMatchingSubject] = (Array.isArray(styles) ? styles : []).filter(
    (style) => (style || '').toLowerCase() !== (subject || '').toLowerCase()
  )
  // Styles is the only param that can be undefined, so we create this helper const to manage spaces needed in the final result
  const styleAndSubjectString = [firstStyleNotMatchingSubject, subject].filter(Boolean).join(' ')
  return `Original ${styleAndSubjectString} ${category} by ${artistName}`
}
// Only the first value of styles is used and if it's the same as subject, uses the second value if exists. Discussed in this ticket: https://saatchiart.atlassian.net/browse/SA-4524
// example of result: `Original Fine Art Abstract Painting by John Doe`
export const getPrintAltText = ({
  artistName,
  category,
  subject,
  styles,
}: ImageAltParams): string => {
  const [firstStyleNotMatchingSubject] = (Array.isArray(styles) ? styles : []).filter(
    (style) => (style || '').toLowerCase() !== (subject || '').toLowerCase()
  )
  // Styles is the only param that can be undefined, so we create this helper const to manage spaces needed in the final result
  const styleAndSubjectString = [firstStyleNotMatchingSubject, subject].filter(Boolean).join(' ')
  return `Print of ${styleAndSubjectString} ${category} by ${artistName}`
}

export const isCategorySculpture = (category: string) => {
  return category === ARTWORK_CATEGORIES.SCULPTURE
}

export const isCategoryInstallation = (category: string) => {
  return category === ARTWORK_CATEGORIES.INSTALLATION
}

export const getMediumMaterialText = (
  mediums: ReadonlyArray<string> | string[],
  materials: ReadonlyArray<string> | string[],
  category: string
): string => {
  const medium = mediums.length ? capitalizeString(mediums[0]) : ''
  const material = materials.length ? capitalizeString(materials[0]) : ''

  // If medium and material are the same, just show one
  if (medium && material && medium.toLowerCase() === material.toLowerCase()) {
    return medium
  }

  const isSculptureCategory = isCategorySculpture(category)

  if (isSculptureCategory) {
    const validMethods = [
      '3d printing',
      '3d sculpting',
      'assemblage',
      'carving',
      'casting',
      'modeling',
      'relief',
      'weaving',
    ]

    if (medium && !validMethods.includes(medium.toLowerCase())) {
      return material
    }
  }

  const glue = isSculptureCategory ? 'of' : 'on'
  const mediumText = medium ? `${medium} ${glue} ` : ''
  const materialText = material

  return `${mediumText}${materialText}`
}

export const getMediumLabel = (category: string): string => {
  return isCategorySculpture(category) ? 'Method:' : 'Mediums:'
}
